import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
    baseURL: API_BASE,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('nexusrig_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('nexusrig_token');
            localStorage.removeItem('nexusrig_user');
            window.dispatchEvent(new Event('auth-logout'));
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    verify2FA: (data) => api.post('/auth/verify-2fa', data),
    verifyEmail: (token) => api.get('/auth/verify-email', { params: { token } }),
    resendVerification: (email) => api.post('/auth/resend-verification', { email }),
    getMe: () => api.get('/auth/me'),
    updateProfile: (data) => api.put('/auth/profile', data),
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
    resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
    verifyResetToken: (token) => api.get('/auth/verify-reset-token', { params: { token } }),
    // 2FA management
    get2FAStatus: () => api.get('/auth/2fa/status'),
    setup2FA: () => api.post('/auth/2fa/setup'),
    confirm2FA: (code) => api.post('/auth/2fa/confirm', { code }),
    disable2FA: (password) => api.post('/auth/2fa/disable', { password }),
};

// Products API
export const productsAPI = {
    getAll: (params) => api.get('/products', { params }),
    getById: (id) => api.get(`/products/${id}`),
    getRelated: (id, limit = 8) => api.get(`/products/${id}/related`, { params: { limit } }),
    getCategories: () => api.get('/categories'),
    getFeatured: () => api.get('/products', { params: { featured: true } }),
    getPopular: (limit = 8) => api.get('/products/popular', { params: { limit } }),
    create: (data) => api.post('/products', data),
    update: (id, data) => api.put(`/products/${id}`, data),
    delete: (id) => api.delete(`/products/${id}`),
    bulkImport: (products) => api.post('/products/bulk-import', products),
    importFromGoogleSheet: (sheetUrl) => api.post('/import/google-sheet', { sheet_url: sheetUrl }),
    // Image management
    updateImages: (id, images) => api.put(`/products/${id}/images`, { images }),
    addImage: (id, imageUrl) => api.post(`/products/${id}/images`, { image_url: imageUrl }),
    removeImage: (id, imageUrl) => api.delete(`/products/${id}/images`, { params: { image_url: imageUrl } }),
    findImages: (id) => api.get(`/products/${id}/find-images`),
    resolveComponents: (id) => api.get(`/products/${id}/resolve-components`),
};

// Image Search API
export const imageAPI = {
    search: (query, numImages = 8) => api.post('/images/search', { query, num_images: numImages }),
    getSearchStatus: () => api.get('/images/search-status'),
    batchFetch: (productIds, numImagesPerProduct = 4) => 
        api.post('/products/batch-fetch-images', { product_ids: productIds, num_images_per_product: numImagesPerProduct }),
    upload: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload/image', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

// Cart API
export const cartAPI = {
    get: () => api.get('/cart'),
    add: (productId, quantity = 1) => api.post('/cart/add', { product_id: productId, quantity }),
    update: (productId, quantity) => api.put('/cart/update', { product_id: productId, quantity }),
    remove: (productId) => api.delete(`/cart/remove/${productId}`),
    clear: () => api.delete('/cart/clear'),
    getSuggestions: (limit = 8) => api.get('/cart/suggestions', { params: { limit } }),
    getFrequentlyBought: (limit = 8) => api.get('/cart/frequently-bought', { params: { limit } }),
};

// Wishlist API
export const wishlistAPI = {
    getAll: () => api.get('/wishlist'),
    add: (productId) => api.post(`/wishlist/${productId}`),
    remove: (productId) => api.delete(`/wishlist/${productId}`),
    check: (productId) => api.get(`/wishlist/check/${productId}`),
    getIds: () => api.get('/wishlist/ids'),
};

// Orders API
export const ordersAPI = {
    create: (data) => api.post('/orders', data),
    getAll: () => api.get('/orders'),
    getById: (id) => api.get(`/orders/${id}`),
};

// Notifications API
export const notificationsAPI = {
    getAll: () => api.get('/notifications'),
    getUnreadCount: () => api.get('/notifications/unread-count'),
    markRead: (id) => api.put(`/notifications/${id}/read`),
    markAllRead: () => api.put('/notifications/read-all'),
};

// Support API
export const supportAPI = {
    createTicket: (data) => api.post('/support/tickets', data),
    getMyTickets: () => api.get('/support/tickets'),
    getTicket: (id) => api.get(`/support/tickets/${id}`),
    addMessage: (id, data) => api.post(`/support/tickets/${id}/messages`, data),
    uploadAttachment: (ticketId, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post(`/support/tickets/${ticketId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
};

// Reviews API
export const reviewsAPI = {
    getProductReviews: (productId) => api.get(`/products/${productId}/reviews`),
    createReview: (productId, data) => api.post(`/products/${productId}/reviews`, data),
    updateReview: (productId, data) => api.put(`/products/${productId}/reviews`, data),
    deleteReview: (productId, reviewId) => api.delete(`/products/${productId}/reviews/${reviewId}`),
};

// Admin Support API
export const adminSupportAPI = {
    getAllTickets: (status = null, priority = null) => api.get('/admin/support/tickets', { params: { status, priority } }),
    getTicket: (id) => api.get(`/admin/support/tickets/${id}`),
    updateTicket: (id, data) => api.put(`/admin/support/tickets/${id}`, data),
    addMessage: (id, data) => api.post(`/admin/support/tickets/${id}/messages`, data),
    uploadAttachment: (ticketId, file) => {
        const fd = new FormData();
        fd.append('file', file);
        return api.post(`/admin/support/tickets/${ticketId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    },
    getStats: () => api.get('/admin/support/stats'),
    getCannedResponses: () => api.get('/admin/canned-responses'),
    createCannedResponse: (data) => api.post('/admin/canned-responses', data),
    updateCannedResponse: (id, data) => api.put(`/admin/canned-responses/${id}`, data),
    deleteCannedResponse: (id) => api.delete(`/admin/canned-responses/${id}`),
};

// PC Builder API
export const pcBuilderAPI = {
    getComponents: (type, condition) => api.get(`/pc-builder/components/${type}`, { params: { condition } }),
    checkCompatibility: (components) => api.post('/pc-builder/check-compatibility', components),
    saveBuild: (data) => api.post('/pc-builder', data),
    getBuilds: () => api.get('/pc-builder'),
    deleteBuild: (buildId) => api.delete(`/pc-builder/${buildId}`),
    addBuildToCart: (buildId) => api.post(`/pc-builder/${buildId}/add-to-cart`),
    shareBuild: (buildId) => api.post(`/pc-builder/${buildId}/share`),
    getSharedBuild: (shareId) => api.get(`/pc-builder/shared/${shareId}`),
};

// Payments API
export const paymentsAPI = {
    initialize: (orderId) => api.post('/payments/initialize', { order_id: orderId }),
    getStatus: (orderId) => api.get(`/payments/status/${orderId}`),
    mockComplete: (orderId) => api.post(`/payments/mock-complete/${orderId}`),
    stripeCheckout: (orderId) => api.post('/payments/stripe/create-checkout', { order_id: orderId }),
    stripeVerify: (orderId) => api.post(`/payments/stripe/verify/${orderId}`),
};

// Seed API
export const seedAPI = {
    seed: () => api.post('/seed'),
};

// TechSpecs API
export const techSpecsAPI = {
    search: (query, category = null) => api.post('/techspecs/search', { query, category }),
    getProduct: (productId) => api.get(`/techspecs/product/${productId}`),
    importProduct: (data) => api.post('/techspecs/import', data),
};

// Admin API
export const adminAPI = {
    getAllOrders: (status = null, user_id = null) => api.get('/admin/orders', { params: { status, user_id } }),
    updateOrder: (orderId, data) => api.put(`/admin/orders/${orderId}`, data),
    migrateOrderIds: () => api.post('/admin/orders/migrate-ids'),
    getNotifications: () => api.get('/admin/notifications'),
    markNotificationsRead: () => api.post('/admin/notifications/mark-read'),
    getImageQuality: () => api.get('/admin/image-quality'),
    getAutoFixStatus: () => api.get('/admin/autofix-status'),
    toggleAutoFix: () => api.post('/admin/autofix-toggle'),
    runAutoFixNow: () => api.post('/admin/autofix-run-now'),
    toggleImageApproved: (productId, approved) => api.put(`/admin/products/${productId}/image-approved`, { image_approved: approved }),
    getCoupons: () => api.get('/admin/coupons'),
    createCoupon: (data) => api.post('/admin/coupons', data),
    updateCoupon: (couponId, data) => api.put(`/admin/coupons/${couponId}`, data),
    deleteCoupon: (couponId) => api.delete(`/admin/coupons/${couponId}`),
    getStockAlerts: () => api.get('/admin/stock-alerts'),
    getPaymentSettings: () => api.get('/admin/payment-settings'),
    updatePaymentSettings: (data) => api.put('/admin/payment-settings', data),
    getSecurityDashboard: () => api.get('/admin/security/dashboard'),
    getSecurityEvents: (limit = 50, eventType = null) => api.get('/admin/security/events', { params: { limit, event_type: eventType } }),
    getBlockedIps: () => api.get('/admin/security/blocked-ips'),
    unblockIp: (ip) => api.post('/admin/security/unblock-ip', { ip }),
};

// Payment Settings API (public)
export const paymentSettingsAPI = {
    get: () => api.get('/payment-settings'),
};

// Coupon API (user-facing)
export const couponAPI = {
    apply: (code, cartTotal) => api.post('/coupons/apply', { code, cart_total: cartTotal }),
};

// Stock Alerts API
export const stockAlertsAPI = {
    subscribe: (productId) => api.post('/stock-alerts/subscribe', { product_id: productId }),
    unsubscribe: (productId) => api.delete(`/stock-alerts/${productId}`),
    check: (productId) => api.get(`/stock-alerts/check/${productId}`),
};

// Announcements API
export const announcementsAPI = {
    getActive: () => api.get('/announcements'),
    getAll: () => api.get('/admin/announcements'),
    create: (data) => api.post('/admin/announcements', data),
    update: (id, data) => api.put(`/admin/announcements/${id}`, data),
    delete: (id) => api.delete(`/admin/announcements/${id}`),
};

// Analytics API
export const analyticsAPI = {
    trackEvent: (data) => api.post('/analytics/event', data),
    getOverview: (days = 7) => api.get('/admin/analytics/overview', { params: { days } }),
};

// Content/CMS API
export const contentAPI = {
    getPageContent: (pageName) => api.get(`/content/${pageName}`),
    updatePageContent: (pageName, content) => api.post(`/content/${pageName}`, content),
};

// Scraper API
export const scraperAPI = {
    getCategories: () => api.get('/scraper/categories'),
    getStatus: () => api.get('/scraper/status'),
    startScraping: (category, maxProducts = 50, fetchDetails = false) => 
        api.post('/scraper/start', { category, max_products: maxProducts, fetch_details: fetchDetails }),
    stopScraping: () => api.post('/scraper/stop'),
    getStats: () => api.get('/scraper/stats'),
    getProducts: (category = null, imported = null, limit = 100) => 
        api.get('/scraper/products', { params: { category, imported, limit } }),
    importProduct: (scrapedId, priceHuf, stock = 0, forceReimport = false) => 
        api.post('/scraper/import', { scraped_id: scrapedId, price_huf: priceHuf, stock, force_reimport: forceReimport }),
    checkDuplicate: (scrapedId) => api.get(`/scraper/check-duplicate/${scrapedId}`),
    searchProduct: (query, subcategory = null) => 
        api.post('/scraper/search', { query, subcategory }),
    scrapeFromUrl: (url, subcategory = null) =>
        api.post('/scraper/scrape-url', { url, subcategory }),
    resetImportStatus: (scrapedId) => api.post(`/scraper/reset-import/${scrapedId}`),
    verifyImports: () => api.post('/scraper/verify-imports'),
    deleteProduct: (scrapedId) => api.delete(`/scraper/products/${scrapedId}`),
    clearCategory: (category) => api.delete(`/scraper/category/${category}`),
};

export default api;
